import { APP_CONTEXTS } from "app/model"
import { getStore } from "app/store/store"
import {
  IForm,
  IFormChange,
  IFormCreativeRow,
  IFormDataLoader,
  IFormMediaCreativeForecastRow,
  SELECT_FILTER_VALUES
} from "form/model"
import { formActionCreator } from "form/store/actions"
import { IMedia, IMediaUploadParams } from "media/model"
import { mediaRPO } from "media/repository"
import { createCampaignMedia } from "media/tools/common"
import { getCreativeTypeFromMimeType } from "media/tools/media-type"
import { finalizeChange, getEmptyFormChange, getFinalizedArrayChange } from "./change"
import { getForm } from "./common"
import { getForecastCreativeChange, getSelectedMediaCreativeId } from "./creative-forecast"

export const creativeFormDataLoader: IFormDataLoader = async (form: IForm, onFinish: (error?: string) => any) => {
  try {
    const medias = await mediaRPO.getCampaignMedias(form.tempCampaignId)
    form.creatives.medias = medias
    form.creatives.selectedRequiredMediaCreativeIds = getSelectedMediaCreativeId(form, medias)
    onFinish()
  } catch (error) {
    console.error(error)
    onFinish(`Creative data-loader error ${String(error)}`)
  }
}

export function getCreativeChange(form: IForm, medias: IMedia[]): IFormChange {
  if (form.context.isForecast) {
    return getForecastCreativeChange(form, medias)
  } else {
    return getCampaignCreativeChange(form, medias)
  }
}

export function getCampaignCreativeChange(form: IForm, newMedias: IMedia[]): IFormChange {
  const change: IFormChange = getEmptyFormChange("media.creatives")
  const previousMedias = form.context.initialData ? form.context.initialData.creatives.medias : []
  for (const newMedia of newMedias) {
    const oldMedia = previousMedias.findByProperty("id", newMedia.id)
    if (!oldMedia) {
      if (newMedia.creatives.length === 0) {
        continue
      }
      change.addedItems.push(...newMedia.creatives.map(c => `${newMedia.id}-${c.id}`))
      continue
    } else {
      const oldCreativeIds = oldMedia.creatives.map(c => `${oldMedia.id}-${c.id}`)
      const newCreativeIds = newMedia.creatives.map(c => `${newMedia.id}-${c.id}`)
      const creativesChanges = getFinalizedArrayChange(
        "label not used",
        oldCreativeIds,
        newCreativeIds,
        id => id as string
      )
      if (creativesChanges.addedItems.length > 0) {
        change.addedItems.push(...creativesChanges.addedItems)
      }
      if (creativesChanges.deletedItems.length > 0) {
        change.deletedItems.push(...creativesChanges.deletedItems)
      }
    }
  }
  for (const oldMedia of previousMedias) {
    const newMedia = newMedias.findByProperty("id", oldMedia.id)
    if (!newMedia) {
      if (oldMedia.creatives.length === 0) {
        continue
      }
      change.deletedItems.push(...oldMedia.creatives.map(c => `${oldMedia.id}-${c.id}`))
    }
  }
  return finalizeChange(change)
}

export function getSummaryCreativeRows(medias: IMedia[]): IFormCreativeRow[] {
  const rows: IFormCreativeRow[] = []
  for (const media of medias) {
    if (!media.creatives || media.creatives.length === 0) {
      continue
    }
    for (const creative of media.creatives) {
      rows.push({
        id: `${media.id}-${media.campaignMediaId}-${creative.id}`,
        mediaId: media.id,
        campaignMediaId: media.campaignMediaId as number,
        creativeId: creative.id,
        name: creative.name || "-",
        createdAt: creative.created_at ? new Date(creative.created_at) : null,
        type: getCreativeTypeFromMimeType(creative.mime_type),
        approval_status_id: media.approval_status_id,
        refusal_note: media.refusal_note
      })
    }
  }
  return rows
}

export const createFormCampaignMedia = async (name: string, params?: IMediaUploadParams): Promise<IMedia> => {
  const form = getForm()
  const media = await createCampaignMedia({
    campaignId: form.tempCampaignId,
    mediaName: name,
    ...params
  })
  getStore().dispatch(
    formActionCreator.setCreatives(form.context.appContext, {
      medias: [...form.creatives.medias, media]
    })
  )
  return media
}

export function setCreativeFilter(context: APP_CONTEXTS, filter: SELECT_FILTER_VALUES): void {
  getStore().dispatch(formActionCreator.setCreativeFilter(context, filter))
}

export function getFilteredCreativeRows<T extends IFormMediaCreativeForecastRow>(
  filter: SELECT_FILTER_VALUES,
  rows: T[]
): T[] {
  if (filter === SELECT_FILTER_VALUES.ALL) {
    return rows
  }
  if (filter === SELECT_FILTER_VALUES.SELECTED) {
    return rows.filter(r => r.isUploaded)
  }
  return rows.filter(r => !r.isUploaded)
}
